import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { ThunkDispatch } from "@reduxjs/toolkit";
import { LoadingSpinner } from './../../../components/Icons'
import CustomIconButton from "../../../components/CustomIconButton";
import CustomButton from "../../../components/CustomButton";
import Dropdown from "../../../components/Dropdown";
import { createStatus, mypageUploadFileDelete, mypageUploadFilePost, mypageUploadFilePut } from "../../../reducers/talentMypage";
import FileUploader from "../../../components/FileUploader";
import { formatDate } from "../../../utils/format";
import { TALENT_FILE_TYPE } from "../../../constants/index";
import { create } from "lodash";
import FileItem from "../../../components/FileItem";
import { downloadFileAttachment } from 'utils/util';
import i18n from 'i18next';
import { useSnackbar } from 'notistack';
import CustomToast from '../../../components/CustomToast';

interface ResumePanelProps {
    onClose: () => void;
    type: string;
}

const ResumePanel: React.FC<ResumePanelProps> = ({ onClose, type }) => {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { talent, isLoading, cityOptions } = useSelector((state: any) => state.talentMypage)
    const [isEditing, setIsEditing] = useState(false)
    const [file, setFile] = useState<any>(null);
    const [fileToDisplay, setFileToDisplay] = useState<any>(() => {
        if (talent?.last_company?.uploaded_files?.length > 0) {
            return talent.last_company.uploaded_files.find((file) => file.type === type) || null;
        }
        return null;
    });
    const panelTitle = type === TALENT_FILE_TYPE.RESUME ? "履歴書のアップロード" : type === TALENT_FILE_TYPE.CV ? "職務経歴書のアップロード" : "その他のファイルのアップロード"

    const showToast = (title: string, message: string, variant: string) => {
        enqueueSnackbar("I love snacks.", {
            autoHideDuration: 7000,
            content: (key) => (
            <CustomToast title={title} message={message} variant={variant} snackbarKey={key} closeSnackbar={closeSnackbar} />
            ),
        });
    };

    const handleFileChange = (file:any) => {
        if (file) {
            setFile(file);
            setFileToDisplay((prev) => ({
                ...prev,
                file_name: file.name,
                file_path: URL.createObjectURL(file),
                created_at: null
            }));
            setIsEditing(true);
        }
    }

    const handleClearFile = () => {
        setFile(null);
        setFileToDisplay((prev) => ({
            ...prev,
            file_name: null,
            file_path: null,
            created_at: null
        }));
        setIsEditing(true);
    }

    const handleSubmit = () => {
        if(!fileToDisplay?.id) {
            dispatch(mypageUploadFilePost({
                formData: {
                    file: file,
                    type: type,
                }
            }))
        } else {
            if(file) {
                dispatch(mypageUploadFilePut({
                    formData: {
                        file: file,
                    },
                    id: fileToDisplay?.id
                }))
            } else {
                dispatch(mypageUploadFileDelete({
                    id: fileToDisplay?.id
                }))
            }
        }
        onClose();
    }

    useEffect(() => {
        if (!file && !isEditing && talent?.last_company?.uploaded_files?.length > 0) {
            const resumeFile = talent.last_company.uploaded_files.find((file) => file.type === type);
            setFileToDisplay(resumeFile || null);
        }
    }, [talent, type, file, isEditing])

    const handleDownloadFileAttachment = async (url: string, filename?: string) => {
        try {
            await downloadFileAttachment(url, filename);
        } catch (error) {
            showToast("メッセージ", i18n.t('MSG_GLOBAL_FILE_NOT_EXIST_OR_DELETED'), "error");
        }
    };

    return (
        <div className="w-[392px] max-w-full h-full bg-white rounded-2xl pt-3 alu-panel-box-shadow">
            <div className="flex items-center justify-between gap-2 px-4 mb-1">
                <h2 className="text-xl leading-[1.875rem] font-bold alu-text-base-2">{panelTitle}</h2>
                <CustomIconButton onClick={onClose} customStyle="shrink-0">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <g clipPath="url(#clip0_8574_265)">
                        <path
                            d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                            fill="currentColor"
                        />
                        </g>
                        <defs>
                        <clipPath id="clip0_8574_265">
                            <rect width="24" height="24" fill="white" />
                        </clipPath>
                        </defs>
                    </svg>
                </CustomIconButton>
            </div>
            <p className="text-sm alu-text-base-1 px-4">対応形式：PDF、Excel、Word（最大 32 MB）</p>
            <div className="overflow-y-auto custom-scrollbar h-[calc(100%-40px)] px-4 pb-4">
                <form className="mt-8 w-full flex flex-col">

                    {!fileToDisplay?.file_name ? (
                        <FileUploader onFileChange={handleFileChange} />
                    ) : (
                        <FileItem
                            file={fileToDisplay}
                            {...(fileToDisplay?.created_at
                                ? { onFileClick: () => handleDownloadFileAttachment(fileToDisplay.file_path, fileToDisplay.file_name) }
                                : {}
                            )}
                            handleClearFile={handleClearFile}
                        />
                    )}
                    
                    <div className="flex items-center justify-between gap-4 mt-10">
                        <CustomButton
                            type="button"
                            onClick={() => onClose()}
                            width="full"
                            variant="outline"
                            size="md"
                        >
                            キャンセル
                        </CustomButton>
                        <CustomButton
                            type="button"
                            onClick={() => handleSubmit()}
                            disabled={isLoading || !isEditing || (!fileToDisplay?.id && !file)}
                            width="full"
                            size="md"
                        >
                            <p>{isLoading ? "保存中..." : "保存"}</p>
                            {isLoading && (
                                <span><LoadingSpinner /></span>
                            )}
                        </CustomButton>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResumePanel;